import { SelectItemV2, findItemInList } from '@schenck/rotec-components';
import { CustomDateAdapter } from '../datetime-picker/datetime-picker.component';

export interface DateRange {
  /**
   * Start date of range.
   */
  from: string | Date;
  /**
   * End date of range.
   */
  to: string | Date;
}

export interface RangeAndInterval {
  /**
   * Time range of statistics.
   */
  range: DateRange;
  /**
   * After how many seconds to refresh statistics.
   */
  interval: number;
}

export const intervalDefault = 60;
export const intervalOneDay = 86400;
export const intervalOptions: SelectItemV2<number>[] = [
  { name: 'MC_30s', value: 30 },
  { name: 'MC_1m', value: intervalDefault },
  { name: 'MC_5m', value: 300 },
  { name: 'MC_15m', value: 900 },
  { name: 'MC_30m', value: 1800 },
  { name: 'MC_1h', value: 3600 },
  { name: 'MC_2h', value: 7200 },
  { name: 'MC_1d', value: intervalOneDay },
];

export function findIntervalOption(value: number): SelectItemV2<number> {
  return findItemInList(intervalOptions, value);
}

export interface QuickRangeDef {
  name: string;
  value: DateRange;
}

export const quickRangeDefault: DateRange = { from: 'now-8h', to: 'now' };
export const quickRangeLast30Days: DateRange = { from: 'now-30d', to: 'now' };
export const quickRanges: QuickRangeDef[] = [
  { name: 'MC_Last 30 minutes', value: { from: 'now-30m', to: 'now' } },
  { name: 'MC_Last 1 hour', value: { from: 'now-1h', to: 'now' } },
  { name: 'MC_Last 8 hours', value: quickRangeDefault },
  { name: 'MC_Last 24 hours', value: { from: 'now-24h', to: 'now' } },
  { name: 'MC_Last 7 days', value: { from: 'now-7d', to: 'now' } },
  { name: 'MC_Last 30 days', value: quickRangeLast30Days },
  { name: 'MC_Last 6 months', value: { from: 'now-6M', to: 'now' } },
  { name: 'MC_Last 1 year', value: { from: 'now-12M', to: 'now' } },
  { name: 'MC_Today', value: { from: 'start of day', to: 'end of day' } },
  { name: 'MC_Today so far', value: { from: 'start of day', to: 'now' } },
  { name: 'MC_This week', value: { from: 'start of week', to: 'end of week' } },
  { name: 'MC_This week so far', value: { from: 'start of week', to: 'now' } },
  { name: 'MC_This month', value: { from: 'start of month', to: 'end of month' } },
  { name: 'MC_This month so far', value: { from: 'start of month', to: 'now' } },
  { name: 'MC_This year', value: { from: 'start of year', to: 'end of year' } },
  { name: 'MC_This year so far', value: { from: 'start of year', to: 'now' } },
];

export function findQuickRange(range: DateRange): QuickRangeDef {
  return quickRanges.find((qr) => qr.value.from === range.from && qr.value.to === range.to);
}

export function getQuickRangeSID(range: DateRange, dateAdapter: CustomDateAdapter): string {
  const qr = findQuickRange(range);
  if (qr) {
    return qr.name;
  }
  const from = typeof range.from === 'string' ? range.from : dateAdapter.format(range.from);
  const to = typeof range.to === 'string' ? range.to : dateAdapter.format(range.to);
  return from + ' - ' + to;
}
